import {
    FC, useMemo, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import VoterCardFrontImage from '../../Assets/images/voter_card_2.png';
import PassportBackImage from '../../Assets/images/passport_back.png';
import DrivingLicenseBackImage from '../../Assets/images/driving_license_back.png';
import OthersImage from '../../Assets/images/others_front.png';
import TakeDocumentPhoto, { ITakeDocumentPhotoInstructions } from '../../Components/TakeDocumentPhoto/TakeDocumentPhoto';
import DI from '../../../Utils/DI';
import Constants, { DOCUMENT_ORIENTATION, DOCUMENT_TYPES } from '../../../Utils/Constants';
import { IFormStepProps } from '../PageWrapper/PageWrapper';
import IChallengeManager from '../../../Business/IChallengeManager';
import IDocumentManager from '../../../Business/IDocumentManager';
import IUserManager from '../../../Business/UserManager/IUserManager';
import useGaEventTracker from '../../Hooks/useGaEventTracker';
import IAnalyticsManager from '../../../Business/IAnalyticsManager';
import useStepContext from '../../Hooks/useStepContext';
import Utils from '../../../Utils/Utils';
import ISessionManager from '../../../Business/ISessionManager';
import { ITempError } from '../AgentLogin/agent-login.types';
import { ERROR_CODES, ERRORS_DESCRIPTION } from '../../../Utils/Constants/Errors';

const StandardInstructionDetailsBack: FC<IFormStepProps> = ({ nextStepHandler }) => {
    const { t } = useTranslation();

    const documentManager: IDocumentManager = DI.get('DocumentManager');
    const userManager: IUserManager = DI.get('UserManager');
    const challengeManager: IChallengeManager = DI.get('ChallengeManager');
    const {
        currentFormStep, whitelistedUser, documentType, setDocumentType, captureBackSide
    } = useStepContext();

    const [error, setError] = useState<ITempError>({
        hasError: false,
        errorCode: NaN
    });

    const { PROPS } = currentFormStep || {};
    const selectedDocumentId = PROPS?.selectedDocumentId;
    const selectedDocumentEn = PROPS?.selectedDocumentEn;
    const selectedDocumentFr = PROPS?.selectedDocumentFr;
    const normalizeToBackType = (type: string): string => {
        const knownTypes: Record<string, string> = {
            NEW_VOTER_CARD: 'VOTER_CARD_2',
            OLD_VOTER_CARD: 'VOTER_CARD_1',
            DRIVING_LICENSE: 'DRIVING_LICENSE',
            DRIVING_LICENSE_BACK: 'DRIVING_LICENSE',
            PASSPORT: 'PASSPORT',
            PASSPORT_BACK: 'PASSPORT'
        };

        return knownTypes[type] || 'OTHERS';
    };
    const backTranslationMap: Record<string, string> = {
        VOTER_CARD_1: 'voterCardBackV1',
        VOTER_CARD_2: 'voterCardBackV2',
        DRIVING_LICENSE: 'drivingLicenseBack',
        PASSPORT: 'passportBack',
        OTHERS: 'othersBack'
    };

    const normalizedType = normalizeToBackType(documentType ?? '');
    const translationKey = backTranslationMap[normalizedType] || 'passportBack';

    const instructionsArray: string[] = [];
    for (let i = 1; i <= 10; i++) {
        const key = `${translationKey}.instruction${i}`;
        const value = t(key, '');
        if (value && value !== key) {
            instructionsArray.push(value);
        }
    }

    const instructions = {
        message: t(`${translationKey}.message`),
        buttonText: t(`${translationKey}.buttonText`),
        captureTitle: t(`${translationKey}.captureTitle`),
        validateTitle: t(`${translationKey}.validateTitle`),
        validateSubtitle: t(`${translationKey}.validateSubtitle`, ''),
        instructionText: t(`${translationKey}.instructionText`) || t('defaultInstructionText')
    };
    const documentImageMap: Record<string, string> = {
        NEW_VOTER_CARD: VoterCardFrontImage,
        OLD_VOTER_CARD: VoterCardFrontImage,
        PASSPORT: PassportBackImage,
        DRIVING_LICENSE: DrivingLicenseBackImage,
        OTHERS: OthersImage
    };
    const documentImage = documentImageMap[documentType ?? ''] || OthersImage;

    const takeDocumentPhotoInstructions: ITakeDocumentPhotoInstructions = {
        key: translationKey,
        instructionsLength: instructionsArray.length
    };
    const { screenTracker, eventTracker } = useGaEventTracker();
    const analyticsManager: IAnalyticsManager = DI.get('AnalyticsManager');
    const sessionManager: ISessionManager = DI.get('SessionManager');
    const isAgentLogged: boolean | undefined = useMemo(() => sessionManager.getSession()?.getIsValid(), []);

    const onChangeFunction = (file: File) => {
        if (!documentType) {
            console.error('Missing document type, cannot proceed with document processing.');
            return;
        }
        documentManager.processDocumentData(
            String(userManager.getMsisdn()),
            file,
            documentType,
            DOCUMENT_ORIENTATION.BACK,
            whitelistedUser
        )
            .then(
                () => {
                    const elapsedTime = moment()
                        .subtract(moment.unix(analyticsManager.getDocumentUploadInitTime()).unix(), 'milliseconds').format('mm:ss');
                    eventTracker({
                        name: 'OCR',
                        objectValue: {
                            SUCCESS: `TOC: ${currentFormStep.KEY} - ${Utils.getTimeInterval(elapsedTime)}`
                        }
                    });
                    eventTracker({
                        name: 'OCR',
                        objectValue: {
                            SUCCESS: `ATTEMPTS - ${currentFormStep.KEY} - ${analyticsManager.getDocumentUploadAttemptsCounter()}`
                        }
                    });
                    analyticsManager.setDocumentUploadAttemptsCounter(1);

                    if (challengeManager.retrieveCapturedSelfie()) {
                        screenTracker({ screen: '6.REVIEW_DETAILS', screenClass: 'GENERAL_FORM' });
                        nextStepHandler({
                            ...Constants.FORM_STEPS.STANDARD_REVIEW_DETAILS,
                            PROPS: { documentType }
                        });
                    } else {
                        screenTracker({ screen: '5.LIVENESS_CHECK', screenClass: 'LIVENESS_CHECK' });
                        if (isAgentLogged) {
                            nextStepHandler({
                                ...Constants.FORM_STEPS.CUSTOMER_VALIDATION,
                                PROPS: { documentType }
                            });
                            return;
                        }
                        nextStepHandler({
                            ...Constants.FORM_STEPS.SELFIE,
                            PROPS: { documentType }
                        });
                    }
                }
            ).catch((errorCode) => {
                const numericCode = Number(errorCode);
                const isServerDown = [500, 503, 504].includes(numericCode);
                if (isServerDown) {
                    ERRORS_DESCRIPTION[numericCode] = {
                        title: t('error.serviceUnavailable'),
                        description: t('error.serviceUnavailableSubtitle')
                    };

                    setError({
                        hasError: true,
                        errorCode: numericCode
                    });
                    return;
                }
                if (!numericCode || isNaN(numericCode)) {
                    ERRORS_DESCRIPTION[ERROR_CODES.NETWORK_ERROR] = {
                        title: t('error.networkError'),
                        description: t('error.networkErrorSubtitle')
                    };

                    setError({
                        hasError: true,
                        errorCode: ERROR_CODES.NETWORK_ERROR
                    });
                    return;
                }

                setError((prev) => ({ ...prev, hasError: true, errorCode }));
            });
    };

    return (
        <>
            <TakeDocumentPhoto
                title={instructions.message}
                image={documentImage}
                captureTitle={instructions.captureTitle}
                validateTitle={instructions.validateTitle}
                instructionText={instructions.instructionText}
                validateSubtitle={instructions.validateSubtitle}
                buttonText={instructions.buttonText}
                onChangeFn={onChangeFunction}
                takeDocumentPhotoInstructions={takeDocumentPhotoInstructions}
                instructions={instructionsArray}
            />
        </>
    );
};

export default StandardInstructionDetailsBack;
